import { BookOpen } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RequiredLabel } from "@/components/catalog/RequiredLabel";
import { JournalNumberField, JournalPlainTextField } from "@/components/catalog/JournalFieldParts";
import type { JournalFields } from "@/pages/admin/catalog/journal-catalog-form";
import { cn } from "@/lib/utils";

interface JournalHierarchyFieldsProps {
  value: JournalFields;
  onChange: (value: JournalFields) => void;
  errors: Record<string, string | undefined>;
  embedded?: boolean;
  idPrefix?: string;
}

export function JournalHierarchyFields({
  value,
  onChange,
  errors,
  embedded = false,
  idPrefix = "journal",
}: JournalHierarchyFieldsProps) {
  const update = <K extends keyof JournalFields>(key: K, next: JournalFields[K]) =>
    onChange({ ...value, [key]: next });
  const volumeNumberId = `${idPrefix}-volume-number`;
  const volumeOnlyId = `${idPrefix}-volume-only`;
  const issueDateId = `${idPrefix}-issue-date`;

  const body = (
    <div className="space-y-5">
      {!embedded ? (
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <BookOpen className="h-5 w-5 text-primary" />
            Volume and issue
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Copies are attached to this structured holding, not free-text series information.
          </p>
        </div>
      ) : null}
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <RequiredLabel htmlFor={volumeNumberId} required>
            Volume number
          </RequiredLabel>
          <Input
            id={volumeNumberId}
            value={value.volumeNumber}
            onChange={(event) => update("volumeNumber", event.target.value)}
            placeholder="e.g., 43"
            className={cn("mt-1.5", errors.volumeNumber && "border-red-500")}
            aria-invalid={Boolean(errors.volumeNumber)}
            aria-describedby={errors.volumeNumber ? `${volumeNumberId}-error` : undefined}
          />
          {errors.volumeNumber ? (
            <p id={`${volumeNumberId}-error`} className="mt-1 text-sm text-red-600">
              {errors.volumeNumber}
            </p>
          ) : null}
        </div>
        <JournalPlainTextField
          id={`${idPrefix}-volume-year`}
          label="Volume publication year"
          value={value.volumePublicationYear}
          onChange={(next) => update("volumePublicationYear", next)}
          placeholder="e.g., 2024"
        />
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id={volumeOnlyId}
          checked={value.volumeOnly}
          onCheckedChange={(checked) => update("volumeOnly", checked === true)}
        />
        <Label htmlFor={volumeOnlyId}>Volume only (no issue)</Label>
      </div>
      {!value.volumeOnly ? (
        <div className="space-y-4 border-t border-gray-200 pt-4">
          <div className="grid gap-4 md:grid-cols-2">
            <JournalNumberField
              id={`${idPrefix}-issue-from`}
              label="Issue number from"
              value={value.issueNumberFrom}
              onChange={(next) => update("issueNumberFrom", next)}
              placeholder="e.g., 1"
            />
            <JournalNumberField
              id={`${idPrefix}-issue-to`}
              label="Issue number to"
              value={value.issueNumberTo}
              onChange={(next) => update("issueNumberTo", next)}
              placeholder="e.g., 2"
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <JournalPlainTextField
              id={`${idPrefix}-issue-year`}
              label="Issue publication year"
              value={value.issuePublicationYear}
              onChange={(next) => update("issuePublicationYear", next)}
              placeholder="e.g., 2024"
            />
            <div>
              <Label htmlFor={issueDateId}>Issue publication date</Label>
              <Input
                id={issueDateId}
                type="date"
                value={value.issuePublicationDate ?? ""}
                onChange={(event) => update("issuePublicationDate", event.target.value)}
                className="mt-1.5"
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
  if (embedded) return body;
  return (
    <Card className="overflow-hidden bg-white shadow-sm">
      <div className="p-6">{body}</div>
    </Card>
  );
}
